import styled from "styled-components"
import {
  REVIEW_REASON_COPY,
  REVIEW_REASON_FALLBACK_COPY,
} from "~constants/proofOfOwnership"
import { SuggestedHolding } from "./AIValidationSuggestionBox"

interface AIAuthenticityConfirmBoxProps {
  reviewReason: string
  onAcceptSuggestion: () => void
  isProcessing: boolean
  // Disabled once the operator edits the form away from the suggested values.
  disabled?: boolean
  // The pre-filled holdings, shown inline so the operator sees what was
  // extracted without scrolling down to the Holdings section.
  holdings?: SuggestedHolding[]
}

// How many holdings to spell out before collapsing the rest into "+N more".
const MAX_HOLDINGS_SHOWN = 6

// Single amber box for authenticity-gate review reasons (RECAPTURE_SUSPECTED,
// SUSPECTED_NON_STATEMENT, DUPLICATE_DOCUMENT): the document was extracted
// (there IS data to accept), but the operator must confirm authenticity first.
// Unlike the plain "validate" reasons (LOW_CONFIDENCE, ENTITY_HOLDER, ...),
// this does not split into a separate yellow note + green accept box — the
// warning and the "Accept & Apply All" action live in one amber box so the
// operator can't miss the authenticity check while accepting.
const AIAuthenticityConfirmBox = ({
  reviewReason,
  onAcceptSuggestion,
  isProcessing,
  disabled = false,
  holdings = [],
}: AIAuthenticityConfirmBoxProps) => {
  const copy = REVIEW_REASON_COPY[reviewReason] || REVIEW_REASON_FALLBACK_COPY
  const shown = holdings.slice(0, MAX_HOLDINGS_SHOWN)
  const extra = holdings.length - shown.length

  return (
    <Container>
      <Icon aria-hidden>⚠️</Icon>
      <Text title={copy}>{copy}</Text>
      <ActionButton
        onClick={onAcceptSuggestion}
        disabled={isProcessing || disabled}
        title={
          disabled
            ? "Revert your edits to the suggested values to accept"
            : undefined
        }
      >
        {isProcessing ? "Processing..." : "Accept & Apply All"}
      </ActionButton>
      {disabled && !isProcessing ? (
        <EditedHint>Edited — revert to accept</EditedHint>
      ) : holdings.length > 0 ? (
        <Holdings>
          <HoldingsLabel>Pre-filled</HoldingsLabel>
          {shown.map(h => (
            <Chip key={h.label}>
              <ChipCode>{h.label}</ChipCode>
              {h.quantity.toLocaleString()}
            </Chip>
          ))}
          {extra > 0 && <More>+{extra} more</More>}
        </Holdings>
      ) : null}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
`

const Icon = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  line-height: 1;
`

// Truncated to one line (full text on hover) so the box stays a fixed
// compact height regardless of copy length.
const Text = styled.div`
  flex: 0 1 auto;
  min-width: 80px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 400;
  color: #92400e;
`

const ActionButton = styled.button`
  flex-shrink: 0;
  background: #b45309;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 0.15s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Holdings = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;
`

const HoldingsLabel = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #b45309;
`

const Chip = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
`

const ChipCode = styled.span`
  font-weight: 700;
  color: #b45309;
`

const More = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: #b45309;
`

const EditedHint = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-weight: 500;
  color: #92400e;
`

export default AIAuthenticityConfirmBox
